import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DndContext, DragOverlay, MouseSensor, pointerWithin, TouchSensor, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EscalationTemplatePlaceholders, TemplatePlaceholders } from '@iotv/datamodel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import NotesIcon from '@mui/icons-material/Notes';
import { Autocomplete, Button, Paper, Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { ActionIcons } from '../../../Iconography';
import { AutosizedPopper } from '../../common/AutosizedPopper';
import { AutosizedTextField } from '../../common/AutosizedTextField';
const SentenceCase = (value) => value.replaceAll('_', ' ');
const UndoSentenceCase = (value) => value.replaceAll(' ', '_');
const DeleteId = 'delete';
const MessageTemplateItem = forwardRef((props, ref) => {
    const { variant = 'normal', disabled = false, value, placeholder, id, setTemplate, isDragging, withOpacity, hoveringDelete, style, attributes, listeners, ...rest } = props;
    const placeholders = variant === 'normal' ? TemplatePlaceholders : EscalationTemplatePlaceholders;
    const inlineStyles = {
        opacity: withOpacity ? '0.5' : '1',
        transformOrigin: '50% 50%',
        ...style,
    };
    let inputField;
    if (!Object.values(placeholders).includes(value))
        inputField = (_jsx(AutosizedTextField, { placeholder: "Text", value: value, error: hoveringDelete, disabled: disabled, color: hoveringDelete ? 'error' : undefined, onChange: (event) => {
                setTemplate((t) => {
                    const itemIndex = t.findIndex((item) => item.id === id);
                    return t.toSpliced(itemIndex, 1, {
                        id: id,
                        value: event.target.value,
                    });
                });
            } }));
    else
        inputField = (_jsx(Autocomplete, { value: value, options: Object.keys(placeholders).map(SentenceCase), isOptionEqualToValue: (option, value) => `[${option}]` === value, onChange: (_, value) => {
                if (value === null)
                    return;
                setTemplate((t) => {
                    const itemIndex = t.findIndex((item) => item.id === id);
                    return t.toSpliced(itemIndex, 1, {
                        id: id,
                        value: placeholders[UndoSentenceCase(value)],
                    });
                });
            }, freeSolo: false, size: "small", disabled: disabled, disableClearable: true, getOptionLabel: (option) => placeholder ?? option, renderOption: (props, option, state) => (_jsx("li", { ...props, ...state, children: option })), slots: { popper: AutosizedPopper }, renderInput: (params) => (_jsx(AutosizedTextField, { ...params, label: 'Placeholder', value: placeholder, error: hoveringDelete, color: hoveringDelete ? 'error' : undefined })) }));
    return (_jsx("div", { ref: ref, style: inlineStyles, ...rest, children: _jsxs(Paper, { elevation: isDragging ? 8 : 0, sx: {
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                transitionProperty: 'all',
                transitionDuration: '0.12s',
                transitionTimingFunction: 'ease-in-out',
                opacity: hoveringDelete ? 0.5 : undefined,
                color: hoveringDelete ? 'error.main' : undefined,
                outlineWidth: hoveringDelete ? 5 : 0,
                outlineOffset: hoveringDelete ? 5 : 0,
                outlineStyle: hoveringDelete ? 'dashed' : 'solid',
            }, children: [_jsx(Stack, { ...listeners, ...attributes, direction: "row", alignItems: "center", sx: {
                        transitionProperty: 'opacity',
                        transitionDuration: '0.3s',
                        transitionTimingFunction: 'ease-in-out',
                        opacity: isDragging ? 0.75 : 0.5,
                        '&:hover': { opacity: isDragging ? 1 : 0.75 },
                        cursor: !disabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    }, children: _jsx(DragIndicatorIcon, {}) }), inputField] }) }));
});
const SortableMessageTemplateItem = (props) => {
    const { isDragging, attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id, data: { value: props.value } });
    const { attributes: _, ...rest } = props;
    const style = {
        transform: CSS.Translate.toString(transform),
        transition: transition || undefined,
    };
    return (_jsx(MessageTemplateItem, { ref: setNodeRef, style: style, withOpacity: isDragging, attributes: !props.disabled ? attributes : {}, listeners: !props.disabled ? listeners : {}, ...rest }));
};
const DeleteZone = ({ active, variant }) => {
    const { setNodeRef } = useDroppable({ id: DeleteId });
    return (_jsx("div", { ref: setNodeRef, children: _jsx(Button, { size: "medium", variant: variant, disabled: !active, color: "error", startIcon: _jsx(ActionIcons.Delete, { color: variant === 'outlined' && active ? 'error' : 'inherit' }), children: "Drag here to delete" }) }));
};
export const MessageTemplate = ({ rulesetTemplate, setRulesetTemplate, variant = 'normal', disabled = false }) => {
    // initial value: template (if set), otherwise static content (if set), otherwise empty
    const [template, setTemplate] = useState(rulesetTemplate && rulesetTemplate.length !== 0
        ? Object.values(rulesetTemplate).map((value) => {
            return { id: v4(), value: value };
        })
        : []);
    const [deleteVariant, setDeleteVariant] = useState('outlined');
    useEffect(() => {
        setRulesetTemplate(template.map(({ value }) => value));
    }, [template]);
    const placeholders = Object.fromEntries(Object.entries(variant === 'normal' ? TemplatePlaceholders : EscalationTemplatePlaceholders).map(([k, v]) => [`[${k}]`, v]));
    // dnd-kit
    const [dragId, setDragId] = useState(null);
    const [dragValue, setDragValue] = useState(null);
    const dragSensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
    const dragStart = useCallback((event) => {
        setDragValue(event.active.data.current?.value);
        setDragId(event.active.id.toString());
    }, []);
    const dragOver = useCallback((event) => {
        const { active, over } = event;
        if (over && over.id === DeleteId) {
            setDeleteVariant('contained');
        }
        else {
            setDeleteVariant('outlined');
        }
        if (over && active.id !== over.id && over.id !== DeleteId) {
            // do swap
            setTemplate((t) => {
                const oldIndex = t.findIndex(({ id }) => id === active.id);
                const newIndex = t.findIndex(({ id }) => id === over.id);
                return arrayMove(t, oldIndex, newIndex);
            });
        }
    }, []);
    const dragCancel = useCallback(() => {
        setDragId(null);
        setDragValue(null);
        setDeleteVariant('outlined');
    }, []);
    const dragEnd = useCallback((event) => {
        const { active, over } = event;
        if (over && over.id === DeleteId) {
            setTemplate((t) => {
                const itemIndex = t.findIndex(({ id }) => id === active.id);
                return t.toSpliced(itemIndex, 1);
            });
        }
        dragCancel();
    }, []);
    function disableSortingStrategy() {
        return null;
    }
    return (_jsxs(DndContext, { sensors: dragSensors, 
        // https://codesandbox.io/p/sandbox/test-dnd-kit-forked-rhsq9?file=%2Fsrc%2FApp.js
        // special handling for multi-line flexbox-arranged items of varying size
        collisionDetection: pointerWithin, onDragStart: dragStart, onDragOver: dragOver, onDragEnd: dragEnd, onDragCancel: dragCancel, children: [_jsx(SortableContext, { items: template, strategy: disableSortingStrategy, children: _jsxs(Stack, { direction: "row", flexWrap: "wrap", alignItems: "center", gap: 1, sx: { marginBlockStart: 0 < template.length ? 1 : 0, marginInlineStart: 0 < template.length ? 0 : 1 }, children: [template.length === 0 && _jsx(Typography, { sx: { fontStyle: 'italic', opacity: 0.75 }, children: "No template elements" }), template.map((entry) => {
                            const { id, value } = entry;
                            return (_jsx(SortableMessageTemplateItem, { id: id, variant: variant, disabled: disabled, value: value, placeholder: value ? placeholders[value] : undefined, setTemplate: setTemplate }, id));
                        }), _jsx(Button, { variant: "outlined", disabled: disabled, startIcon: _jsx(HighlightAltIcon, {}), onClick: () => {
                                setTemplate((t) => t.concat({ id: v4(), value: variant === 'normal' ? TemplatePlaceholders.Ruleset : EscalationTemplatePlaceholders.Notification_Person }));
                            }, children: "Add placeholder" }), _jsx(Button, { variant: "outlined", disabled: disabled, startIcon: _jsx(NotesIcon, {}), onClick: () => {
                                setTemplate((t) => t.concat({ id: v4(), value: '' }));
                            }, children: "Add text" }), _jsx(DeleteZone, { active: dragId !== null, variant: deleteVariant })] }) }), _jsx(DragOverlay, { style: { transformOrigin: '0 0' }, children: dragId && dragValue ? (_jsx(MessageTemplateItem, { id: dragId, variant: variant, disabled: disabled, value: dragValue, placeholder: placeholders[dragValue], setTemplate: setTemplate, isDragging: true, hoveringDelete: deleteVariant === 'contained' })) : undefined })] }));
};
